import { objectWithoutProperties as _objectWithoutProperties, defineProperty as _defineProperty, extends as _extends, objectSpread2 as _objectSpread2, taggedTemplateLiteral as _taggedTemplateLiteral } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import { ClassNames } from '@emotion/react';
import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import { Transition } from 'react-transition-group';
import useForkRef from '../../../hooks/use-fork-ref.esm.js';
import { useSafeThemeUI } from '../../../theme-provider/use-theme-ui.esm.js';
import { duration } from '../../../themes/theme-keys/transitions.esm.js';
import Box from '../../_primitives/box/box.esm.js';
import Flex from '../../_primitives/flex/flex.esm.js';
import { getTransitionProps } from '../transitions/utils.esm.js';

var _templateObject, _templateObject2, _templateObject3;
var _excluded = ["children", "collapsedSize", "component", "easing", "in", "onEnter", "onEntered", "onEntering", "onExit", "onExited", "onExiting", "addEndListener", "style", "timeout", "orientation", "wrapperProps"];
var styles = function styles(orientation, theme) {
  var _container;
  var isHorizontal = orientation === 'horizontal';
  var size = isHorizontal ? 'width' : 'height';
  return {
    container: (_container = {}, _defineProperty(_container, size, '0'), _defineProperty(_container, "overflow", 'hidden'), _defineProperty(_container, "transition", theme !== null && theme !== void 0 && theme.transitions ? theme.transitions.create(size) : size), _container)
  };
};

/**
 * The Collapse transition is used by the
 * [Vertical Stepper](/components/steppers/#vertical-stepper) StepContent component.
 * It uses [react-transition-group](https://github.com/reactjs/react-transition-group) internally.
 */
var Collapse = /*#__PURE__*/React.forwardRef(function Collapse(props, ref) {
  var children = props.children,
    _props$collapsedSize = props.collapsedSize,
    collapsedSizeProp = _props$collapsedSize === void 0 ? '0px' : _props$collapsedSize,
    _props$component = props.component,
    Component = _props$component === void 0 ? Box : _props$component,
    easing = props.easing,
    inProp = props["in"],
    onEnter = props.onEnter,
    onEntered = props.onEntered,
    onEntering = props.onEntering,
    onExit = props.onExit,
    onExited = props.onExited,
    onExiting = props.onExiting,
    addEndListener = props.addEndListener,
    style = props.style,
    _props$timeout = props.timeout,
    timeout = _props$timeout === void 0 ? duration.standard : _props$timeout,
    _props$orientation = props.orientation,
    orientation = _props$orientation === void 0 ? 'vertical' : _props$orientation,
    wrapperProps = props.wrapperProps,
    other = _objectWithoutProperties(props, _excluded);
  var theme = useSafeThemeUI(props);
  var timer = useRef();
  var wrapperRef = useRef(null);
  var autoTransitionDuration = useRef();
  var isHorizontal = orientation === 'horizontal';
  var size = isHorizontal ? 'width' : 'height';
  var collapsedSize = typeof collapsedSizeProp === 'number' ? "".concat(collapsedSizeProp, "px") : collapsedSizeProp;
  useEffect(function () {
    return function () {
      clearTimeout(timer.current);
    };
  }, []);
  var nodeRef = useRef(null);
  var handleRef = useForkRef(ref, nodeRef);
  var normalizedTransitionCallback = function normalizedTransitionCallback(callback) {
    return function (maybeIsAppearing) {
      if (callback) {
        var node = nodeRef.current;

        // onEnterXxx and onExitXxx callbacks have a different arguments.length value.
        if (maybeIsAppearing === undefined) {
          callback(node);
        } else {
          callback(node, maybeIsAppearing);
        }
      }
    };
  };
  var getWrapperSize = function getWrapperSize() {
    return wrapperRef.current ? wrapperRef.current[isHorizontal ? 'clientWidth' : 'clientHeight'] : 0;
  };
  var handleEnter = normalizedTransitionCallback(function (node, isAppearing) {
    if (wrapperRef.current && isHorizontal) {
      // Set absolute position to get the size of collapsed content
      wrapperRef.current.style.position = 'absolute';
    }
    node.style[size] = collapsedSize;
    if (onEnter) {
      onEnter(node, !!isAppearing);
    }
  });
  var handleEntering = normalizedTransitionCallback(function (node, isAppearing) {
    var wrapperSize = getWrapperSize();
    // eslint-disable-next-line no-console
    // console.log(`handleEntering wrapperSize ${wrapperSize}`)

    if (wrapperRef.current && isHorizontal) {
      // After the size is read reset the position back to default
      wrapperRef.current.style.position = '';
    }
    var _getTransitionProps = getTransitionProps({
        style: style,
        timeout: timeout !== 'auto' ? timeout : duration.standard,
        easing: easing
      }, {
        mode: 'enter'
      }),
      transitionDuration = _getTransitionProps.duration;
    if (timeout === 'auto') {
      var duration2 = theme.transitions ? theme.transitions.getAutoHeightDuration(wrapperSize) : 500;
      node.style.transitionDuration = "".concat(duration2, "ms");
      autoTransitionDuration.current = duration2;
    } else {
      node.style.transitionDuration = typeof transitionDuration === 'string' ? transitionDuration : "".concat(transitionDuration, "ms");
    }
    node.style[size] = "".concat(wrapperSize, "px");
    if (onEntering) {
      onEntering(node, !!isAppearing);
    }
  });
  var handleEntered = normalizedTransitionCallback(function (node, isAppearing) {
    node.style[size] = 'auto';
    // eslint-disable-next-line no-console
    // console.log(`handleEntered`)
    if (onEntered) {
      onEntered(node, !!isAppearing);
    }
  });
  var handleExit = normalizedTransitionCallback(function (node) {
    node.style[size] = "".concat(getWrapperSize(), "px");
    if (onExit) {
      onExit(node);
    }
  });
  var handleExited = normalizedTransitionCallback(function (node) {
    if (onExited) {
      onExited(node);
    }
  });
  var handleExiting = normalizedTransitionCallback(function (node) {
    var wrapperSize = wrapperRef.current ? wrapperRef.current[isHorizontal ? 'clientWidth' : 'clientHeight'] : 0;
    // eslint-disable-next-line no-console
    // console.log(`handleExiting wrapperSize ${wrapperSize}`)
    var _getTransitionProps2 = getTransitionProps({
        style: style,
        timeout: timeout !== 'auto' ? timeout : duration.standard,
        easing: easing
      }, {
        mode: 'exit'
      }),
      transitionDuration = _getTransitionProps2.duration;
    if (timeout === 'auto') {
      var duration2 = theme.transitions ? theme.transitions.getAutoHeightDuration(wrapperSize) : 500;
      node.style.transitionDuration = "".concat(duration2, "ms");
      autoTransitionDuration.current = duration2;
    } else {
      node.style.transitionDuration = typeof transitionDuration === 'string' ? transitionDuration : "".concat(transitionDuration, "ms");
    }
    node.style[size] = collapsedSize;
    if (onExiting) {
      onExiting(node);
    }
  });
  var handleAddEndListener = useCallback(function (next) {
    if (timeout === 'auto') {
      timer.current = setTimeout(next, autoTransitionDuration.current || 0);
    }
    if (addEndListener && nodeRef.current) {
      // Old call signature before `react-transition-group` implemented `nodeRef`
      addEndListener(nodeRef.current, next);
    }
  }, [addEndListener, timeout]);
  var _styles = useMemo(function () {
    return styles(orientation, theme);
  }, [theme, orientation]);
  return /*#__PURE__*/React.createElement(Transition, _extends({
    "in": inProp,
    onEnter: handleEnter,
    onEntered: handleEntered,
    onEntering: handleEntering,
    onExit: handleExit,
    onExited: handleExited,
    onExiting: handleExiting,
    addEndListener: handleAddEndListener,
    nodeRef: nodeRef,
    timeout: timeout === 'auto' ? 500 : timeout
  }, other),
  // @ts-ignore
  function (state, childProps) {
    var componentProps = _objectSpread2(_defineProperty({
      ref: handleRef,
      __css: _styles.container
    }, isHorizontal ? 'minWidth' : 'minHeight', collapsedSize), childProps);
    var exited = state === 'exited' && !inProp && collapsedSize === '0px';
    return /*#__PURE__*/React.createElement(ClassNames, null, function (_ref) {
      var css = _ref.css;
      return /*#__PURE__*/React.createElement(Component, _extends({
        className: state === 'entered' ? isHorizontal ? css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n                            width: auto;\n                            overflow: visible;\n                          "]))) : css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n                            height: auto;\n                            overflow: visible;\n                          "]))) : exited ? css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n                            visibility: hidden;\n                          "]))) : undefined
      }, componentProps), /*#__PURE__*/React.createElement(Flex, {
        ref: wrapperRef,
        className: "cs-tui-collapse-wrapper"
      }, isHorizontal ? /*#__PURE__*/React.createElement(Box, _extends({
        className: "cs-tui-collapse-wrapper-inner",
        width: "auto",
        height: "100%"
      }, wrapperProps), children) : /*#__PURE__*/React.createElement(Box, _extends({
        className: "cs-tui-collapse-wrapper-inner",
        width: "100%"
      }, wrapperProps), children)));
    });
  });
});

export default Collapse;
//# sourceMappingURL=collapse.esm.js.map
